import { isCommunicationUserIdentifier } from '@azure/communication-common';
import {
    MessageProps,
    MessageRenderer,
    MessageThread,
    SendBox,
    useChatThreadClient,
    usePropsFor,
} from '@azure/communication-react';
import { Stack } from '@fluentui/react';
import {
    ChatFileUploadButton,
    chatMessageFooterStyles,
    PcpMessageThreadWrapper,
    PcpSendBox,
    selectIsMobile,
    useChatFileUpload,
    useIsMobileWithSmallHeight,
    usePcpLiveLessonContext,
} from 'live-lesson/shared/feature-call-ui-react';
import React from 'react';
import { useClearThread } from '../../../hooks/useClearCurrentChatThread';
import { ClearChatMessagesButton } from './ClearChatMessagesButton';
import { ModeratorChatMessageRenderer } from './ModeratorChatMessageRenderer';

const clearChatMessagesButtonContainerStyles = {
    paddingLeft: '20px',
};

const messageThreadContainerStyles = {
    overflow: 'hidden',
    flexGrow: 1,
    paddingLeft: '20px',
};


interface ModeratorChatComponentsProps {
    isOpen: boolean;
    threadId: string;

    pcpApiDeleteMessage: (threadId: string, messageId: string, userId: string) => Promise<unknown>;
}

/**
 * Render a single chat thread with a send box.
 * The thread to render is dynamically determined by the chatThreadClient,
 * which is retrieved by the closest ChatThreadClientProvider.
 */
export function ModeratorChatComponents(props: ModeratorChatComponentsProps): JSX.Element {

    const { isOpen, threadId, pcpApiDeleteMessage } = props;

    // see https://azure.github.io/communication-ui-library/?path=/docs/stateful-client-get-started-chat--docs#compose-chat-components

    const messageThreadProps = usePropsFor(MessageThread);
    const sendBoxProps = usePropsFor(SendBox);

    const chatThreadClient = useChatThreadClient();
    const { isClearing, clearCurrentChatThread } = useClearThread();


    const {
        attachmentsWithProgress,
        onCancelFileUpload,
        completedFileUploads,
        resetAttachments,
        selectFileAttachment,
    } = useChatFileUpload();

    const onSendMessage = React.useCallback(async (message: string) => {
        sendBoxProps.onSendMessage(message, {
            metadata: {
                // `fileSharingMetadata` is the key used by UI Library to recognize file attachments in a message.
                // Using a different key will prevent UI Library from recognizing the file attachment and file sharing UI won't work.
                fileSharingMetadata: JSON.stringify(completedFileUploads.current),
            },
        }).catch(console.error);

        resetAttachments();
    }, [sendBoxProps, completedFileUploads, resetAttachments]);

    const onDeleteMessage = React.useCallback((messageId: string) => chatThreadClient.getMessage(messageId).then((message) => {
        const sender = message.sender;
        if (sender && isCommunicationUserIdentifier(sender)) {
            const userId: string = sender.communicationUserId;
            return pcpApiDeleteMessage(threadId, messageId, userId);
        }

        return Promise.reject('Could not delete message');
    }), [chatThreadClient, pcpApiDeleteMessage, threadId]);

    const onRenderMessage = React.useCallback(
        (messageProps: MessageProps, defaultOnRender?: MessageRenderer) => (
            <ModeratorChatMessageRenderer
                messageProps={messageProps}
                defaultOnRender={defaultOnRender}
                onDeleteMessage={onDeleteMessage}
            />
        ),
        // Keep this callback stable. MessageThread re-renders on its own when its `messages`
        // prop changes and passes fresh `messageProps` per row, so grouped messages still
        // re-group on delete without re-keying the callback on every chat event.
        [onDeleteMessage]
    );

    const messageThreadContainerRef = React.useRef<HTMLDivElement>(null);
    const isMobile = usePcpLiveLessonContext(selectIsMobile);
    const isShortScreen = useIsMobileWithSmallHeight();

    return (
        <Stack verticalFill>
            <Stack.Item style={clearChatMessagesButtonContainerStyles}>
                <ClearChatMessagesButton isClearing={isClearing} onClick={clearCurrentChatThread} />
            </Stack.Item>
            <div
                style={messageThreadContainerStyles}
                ref={messageThreadContainerRef}
            >
                {/*Props are updated asynchronously, so only render the component once props are populated.*/}
                {messageThreadProps &&
                    //  TODO: customize attachment rendering:
                    // https://azure.github.io/communication-ui-library/?path=/docs/components-message-thread-display-message-with-attachments--docs#advanced-usage-customizing-attachment-rendering

                    // TODO: render image attachments inline
                    // https://azure.github.io/communication-ui-library/?path=/docs/components-message-thread-tapping-inline-images--docs
                    <PcpMessageThreadWrapper
                        {...messageThreadProps}
                        onRenderMessage={onRenderMessage}
                    />
                }
            </div>
            <Stack.Item styles={chatMessageFooterStyles(isShortScreen)}>
                {sendBoxProps &&
                    <>
                        <Stack horizontal={true}>
                            <Stack.Item>
                                <ChatFileUploadButton onFilesSelected={selectFileAttachment} />
                            </Stack.Item>

                            <Stack.Item grow={true}>
                                <PcpSendBox
                                    {...sendBoxProps}
                                    attachments={attachmentsWithProgress}
                                    onCancelAttachmentUpload={onCancelFileUpload}
                                    onSendMessage={onSendMessage}
                                    autoFocus = {isMobile ? undefined : 'sendBoxTextField'}
                                    isShown = {isOpen}
                                />
                            </Stack.Item>
                        </Stack>

                        {/*
                            NOTE: there's also a `RichTextSendBox` component
                            with advanced formatting support and better attachment display.
                        */}
                        {/*<RichTextSendBox*/}
                        {/*    attachments={attachmentsWithProgress}*/}
                        {/*    onCancelAttachmentUpload={onCancelFileUpload}*/}
                        {/*    onSendMessage={onSendMessage}*/}
                        {/*/>*/}
                    </>
                }
            </Stack.Item>
        </Stack>
    );
}
